import {
    Component, EventEmitter, forwardRef, Input, OnDestroy, OnInit, Output, ViewChild, ElementRef, AfterViewChecked,
    AfterViewInit, HostListener
} from '@angular/core';
import { Overlay, ConnectedPosition, ScrollStrategy, CdkConnectedOverlay } from '@angular/cdk/overlay';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { Observable, Subject, of } from 'rxjs';
import {
    catchError, debounceTime, finalize, switchMap, takeUntil
} from 'rxjs/operators';

@Component({
    selector: 'core-ui-chips-autocomplete-form',
    templateUrl: './chips-autocomplete-form.component.html',
    styleUrls: ['./chips-autocomplete-form.component.scss'],
    standalone: false,
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => ChipsAutocompleteFormComponent),
            multi: true,
        },
    ],
})
export class ChipsAutocompleteFormComponent<T> implements ControlValueAccessor, OnInit, OnDestroy, AfterViewInit, AfterViewChecked {
    constructor(private overlay: Overlay, private elementRef: ElementRef<HTMLElement>) {
        this.overlayScrollStrategy = this.overlay.scrollStrategies.close();
    }
    @Input() label?: string;
    @Input() placeholder = 'Escribe para buscar...';
    @Input() placeholderDropdown = 'Filtrar...';
    @Input() readOnly = false;
    @Input() disabled = false;
    @Input() block = true;
    @Input() minChars = 1;
    @Input() debounceMs = 300;
    @Input() maxItems = 15;
    @Input() useOverlay = false;

    @Input({ required: true }) searchFn!: (term: string) => Observable<T[]>;
    @Input() displayWith: (item: T) => string = (i: any) => i?.label ?? i?.name ?? String(i);
    @Input() trackBy: (item: T) => string | number = (i: any) => (i?.id != null ? i.id : String(i));
    @Input() returnValue: 'id' | 'object' = 'object';

    @Output() added = new EventEmitter<T>();
    @Output() removed = new EventEmitter<T>();

    selectedItems: T[] = [];
    panelOpen = false;
    loading = false;
    results: T[] = [];
    activeIndex = -1;
    filterTerm = '';

    trackByFn = (_: number, item: T) => this.trackBy(item);

    private term$ = new Subject<string>();
    private destroy$ = new Subject<void>();
    private onChange: (v: any) => void = () => { };
    private onTouched: () => void = () => { };

    private idToItem = new Map<string, T>();
    private shouldFocusDropdown = false;
    private ignoreNextDocDown = false;

    @ViewChild('dropdownFilterInput') dropdownFilterInput?: ElementRef<HTMLInputElement>;
    @ViewChild('panelEl', { read: ElementRef }) panelEl?: ElementRef<HTMLElement>;
    @ViewChild('originRef', { read: ElementRef }) originRef?: ElementRef<HTMLElement>;
    @ViewChild(CdkConnectedOverlay) private overlayDir?: CdkConnectedOverlay;

    panelWidth: number | null = null;
    private originResizeObserver: ResizeObserver | null = null;
    readonly overlayScrollStrategy: ScrollStrategy;
    readonly overlayPositions: ConnectedPosition[] = [
        { originX: 'start', originY: 'bottom', overlayX: 'start', overlayY: 'top', offsetY: 4 },
        { originX: 'start', originY: 'top', overlayX: 'start', overlayY: 'bottom', offsetY: -4 },
    ];
    readonly overlayPanelClass = 'core-ui-chips-autocomplete-overlay-pane';

    ngOnInit(): void {
        this.term$
            .pipe(
                debounceTime(this.debounceMs),
                switchMap((raw) => {
                    const term = (raw ?? '').trim();
                    if (term.length < this.minChars) {
                        this.loading = false;
                        this.results = [];
                        this.activeIndex = -1;
                        return of([] as T[]);
                    }
                    this.loading = true;
                    return this.searchFn(term).pipe(
                        catchError(() => of([] as T[])),
                        finalize(() => (this.loading = false))
                    );
                }),
                takeUntil(this.destroy$)
            )
            .subscribe((items) => {
                const filtered = (items ?? []).filter(
                    (item) => !this.isSelected(item)
                );
                this.results = filtered.slice(0, this.maxItems);
                this.activeIndex = this.results.length ? 0 : -1;
            });
    }

    ngAfterViewInit(): void {
        this.setupOriginResizeObserver();
    }

    ngOnDestroy(): void {
        this.originResizeObserver?.disconnect();
        this.originResizeObserver = null;
        this.destroy$.next();
        this.destroy$.complete();
    }

    private setupOriginResizeObserver(): void {
        const originEl = this.originRef?.nativeElement;
        if (!originEl || typeof ResizeObserver === 'undefined') return;

        this.originResizeObserver = new ResizeObserver(() => {
            if (this.panelOpen && this.overlayDir?.overlayRef) {
                this.overlayDir.overlayRef.updatePosition();
            }
        });
        this.originResizeObserver.observe(originEl);
    }

    writeValue(v: any): void {
        if (v == null || (Array.isArray(v) && v.length === 0)) {
            this.selectedItems = [];
            this.idToItem.clear();
            this.emitValue();
            return;
        }
        if (!Array.isArray(v)) {
            this.selectedItems = [];
            this.emitValue();
            return;
        }
        if (this.returnValue === 'object') {
            this.selectedItems = [...v];
        } else {
            this.selectedItems = v
                .map((id) => this.idToItem.get(String(id)))
                .filter((x): x is T => x != null);
        }
        this.emitValue();
    }

    registerOnChange(fn: any): void {
        this.onChange = fn;
    }
    registerOnTouched(fn: any): void {
        this.onTouched = fn;
    }
    setDisabledState(isDisabled: boolean): void {
        this.disabled = isDisabled;
    }

    isSelected(item: T): boolean {
        const key = String(this.trackBy(item));
        return this.selectedItems.some((s) => String(this.trackBy(s)) === key);
    }

    onContainerClick(): void {
        if (this.disabled || this.readOnly) return;
        const originEl = this.originRef?.nativeElement ?? this.elementRef.nativeElement;
        this.panelWidth = originEl?.offsetWidth ?? null;
        this.panelOpen = true;
        this.filterTerm = '';
        this.term$.next('');
        this.shouldFocusDropdown = true;
    }

    ngAfterViewChecked(): void {
        if (this.shouldFocusDropdown && this.panelOpen && this.dropdownFilterInput?.nativeElement) {
            this.shouldFocusDropdown = false;
            setTimeout(() => this.dropdownFilterInput?.nativeElement?.focus(), 0);
        }
    }

    onFilterInput(term: string): void {
        this.filterTerm = term;
        this.panelOpen = true;
        this.term$.next(term);
    }

    onFilterInputFromEvent(ev: Event): void {
        const value = (ev.target as HTMLInputElement)?.value ?? '';
        this.onFilterInput(value);
    }

    onBlur(): void {
        this.onTouched();
    }

    closePanel(): void {
        this.panelOpen = false;
        this.activeIndex = -1;
        this.loading = false;
        this.filterTerm = '';
    }

    onPanelMouseDown(): void {
        this.ignoreNextDocDown = true;
        queueMicrotask(() => (this.ignoreNextDocDown = false));
    }

    @HostListener('document:mousedown', ['$event'])
    onDocMouseDown(ev: MouseEvent): void {
        if (!this.panelOpen) return;
        if (this.ignoreNextDocDown) return;
        const t = ev.target as Node;
        const host = this.elementRef.nativeElement as HTMLElement;
        if (host.contains(t)) return;
        if (this.useOverlay) {
            const pane = document.querySelector(`.${this.overlayPanelClass}`);
            if (pane?.contains(t)) return;
        } else {
            const panel = this.panelEl?.nativeElement;
            if (!!panel && panel.contains(t)) return;
        }
        this.closePanel();
    }

    onOverlayDetach(): void {
        if (this.panelOpen) this.closePanel();
    }

    removeChip(item: T, e: Event): void {
        e.stopPropagation();
        if (this.disabled || this.readOnly) return;
        this.selectedItems = this.selectedItems.filter(
            (s) => String(this.trackBy(s)) !== String(this.trackBy(item))
        );
        this.removed.emit(item);
        this.emitValue();
        if (this.panelOpen) {
            this.term$.next(this.filterTerm ?? '');
        }
    }

    selectItem(item: T, e?: Event): void {
        if (e) {
            e.preventDefault();
            e.stopPropagation();
        }
        if (this.isSelected(item)) return;

        this.selectedItems = [...this.selectedItems, item];
        const key = String(this.trackBy(item));
        this.idToItem.set(key, item);
        this.added.emit(item);
        this.emitValue();
        this.results = this.results.filter((r) => this.trackBy(r) !== this.trackBy(item));
        this.activeIndex = this.results.length ? 0 : -1;

        setTimeout(() => this.dropdownFilterInput?.nativeElement?.focus(), 0);
    }


    onKeydown(e: KeyboardEvent): void {
        if (!this.panelOpen) return;
        const max = this.results.length;
        switch (e.key) {
            case 'ArrowDown':
                if (!max) return;
                e.preventDefault();
                this.activeIndex = (this.activeIndex + 1) % max;
                break;
            case 'ArrowUp':
                if (!max) return;
                e.preventDefault();
                this.activeIndex = (this.activeIndex - 1 + max) % max;
                break;
            case 'Enter':
                if (!max) return;
                e.preventDefault();
                if (this.activeIndex >= 0 && this.activeIndex < max) {
                    this.selectItem(this.results[this.activeIndex]);
                }
                break;
            case 'Escape':
                e.preventDefault();
                this.closePanel();
                break;
        }
    }

    private emitValue(): void {
        if (this.returnValue === 'id') {
            this.onChange(this.selectedItems.map((s) => this.trackBy(s)));
        } else {
            this.onChange([...this.selectedItems]);
        }
    }

    get showMinCharsHint(): boolean {
        const n = (this.filterTerm ?? '').trim().length;
        return this.panelOpen && n > 0 && n < this.minChars && !this.loading;
    }

    get showNoResults(): boolean {
        const n = (this.filterTerm ?? '').trim().length;
        return this.panelOpen && n >= this.minChars && !this.loading && this.results.length === 0;
    }
}
